import { CLOUD } from '../cloud/api.js';
import { trialBanner } from '../cloud/plan.js';
import { addDays, diffDays, dstr, fmtDM, fmtDateLong, fmtH, fromD, loc, money, startOfMonth, startOfWeek, today } from '../dates.js';
import { t } from '../i18n/index.js';
import { collapseGroups, eventsIn, groupLabel, groupMembers, invIsOverdue, meetOf, outstandingTotal, sesEnd, statsRange, stu, stuName, stuTotals } from '../model.js';
import { evStart, homeReminderCta, homeRemindersCard } from '../notify/reminders.js';
import { S, canStore } from '../state.js';
import { IC, RT, avatarHtml, esc, evtLine, render, sheetStack } from '../ui.js';
import { myName, schoolView } from '../team/team.js';
import { vSchoolHome } from './team.js';

/* ---------- relative time: "in 2 h 10 min", "tomorrow", "in progress" ---------- */
export function countdown(startMs,durMin){
  const now=Date.now(),diff=startMs-now;
  if(diff<=0)return now<startMs+(+durMin||60)*6e4?{txt:t('ov.now'),now:true}:{txt:'',past:true};
  const min=Math.round(diff/6e4);
  if(min<60)return{txt:t('ov.inMin',{n:Math.max(1,min)})};
  if(min<12*60){const h=Math.floor(min/60),m=min%60;return{txt:m?t('ov.inHM',{h,m}):t('ov.inH',{h})}}
  const d=new Date(startMs);
  const days=diffDays(today(),dstr(d));
  if(days<=0){const h=Math.round(min/60);return{txt:t('ov.inH',{h})}}
  return{txt:days===1?t('ov.tomorrow'):t('ov.inDays',{n:days})};
}
const dayLabel=date=>{
  const n=diffDays(today(),date);
  if(n===0)return t('c.today');if(n===1)return t('ov.tomorrowCap');
  return fromD(date).toLocaleDateString(loc(),{weekday:'short',day:'numeric',month:'short'});
};
const subjPill=s=>s.subject?' <span class="pill-lang">'+esc(s.subject)+'</span>':'';

/* live countdowns on the overview; re-render once a lesson starts or ends */
let tick=null;
export function startHomeTicker(root){
  clearInterval(tick);
  tick=setInterval(()=>{
    if(RT.v!=='home'||!document.body.contains(root)){clearInterval(tick);return}
    let stale=false;
    root.querySelectorAll('[data-countdown]').forEach(el=>{
      const c=countdown(+el.dataset.countdown,+el.dataset.dur);
      if(c.past||(!!c.now!==el.classList.contains('now')))stale=true;
      el.textContent=c.txt;
    });
    if(stale&&!sheetStack.length)render();
  },20000);
}

export function vHome(){
  if(schoolView())return vSchoolHome();
  const now=new Date();const h=now.getHours();
  const greet=h<12?t('home.morning'):h<18?t('home.afternoon'):t('home.evening');
  const me=myName();const name=me?', '+me.split(' ')[0]:'';
  const T=today();
  const td=eventsIn(T,T);
  const active=S.students.filter(s=>!s.archived);
  // everything still ahead (or running right now), next 120 days
  const ahead=eventsIn(T,addDays(T,120)).filter(e=>e.status==='planned'&&evStart(e)+(+e.dur)*6e4>Date.now()&&stu(e.studentId)&&!stu(e.studentId).archived);
  const next=ahead[0];
  const nextBy=new Map();for(const e of ahead)if(!nextBy.has(e.studentId))nextBy.set(e.studentId,e);
  const who=active.map(s=>({s,e:nextBy.get(s.id)||null}))
    .sort((a,b)=>a.e&&b.e?(a.e.date+a.e.time).localeCompare(b.e.date+b.e.time):a.e?-1:b.e?1:a.s.first.localeCompare(b.s.first));
  const wk0=startOfWeek(T),ws=statsRange(wk0,addDays(wk0,6));
  const m0=startOfMonth(T);const mEnd=(()=>{const d=fromD(m0);d.setMonth(d.getMonth()+1);d.setDate(0);return dstr(d)})();
  const ms=statsRange(m0,mEnd);
  const monthName=fromD(T).toLocaleDateString(loc(),{month:'long'});
  const upc=eventsIn(addDays(T,1),addDays(T,7)).filter(e=>e.status==='planned').slice(0,6);

  // follow-ups
  const fu=[];
  for(const x of S.sessions.filter(x=>x.cancel&&x.cancel.by==='parent'&&!x.cancel.seen).sort((a,b)=>a.date.localeCompare(b.date)).slice(0,4))
    fu.push({html:t('pcx.fu',{name:esc(stuName(x.studentId)),date:esc(dayLabel(x.date)+', '+(x.time||''))}),act:'evt',id:x.id});
  for(const x of S.sessions.filter(x=>x.booked&&x.booked.by==='parent'&&!x.booked.seen).sort((a,b)=>a.date.localeCompare(b.date)).slice(0,4))
    fu.push({html:t('mk.fu',{name:esc(stuName(x.studentId)),date:esc(dayLabel(x.date)+', '+(x.time||''))}),act:'evt',id:x.id});
  const noRecap=S.sessions.filter(s=>s.status==='done'&&!s.summary&&s.date>=addDays(T,-14)).sort((a,b)=>b.date.localeCompare(a.date)).slice(0,3);
  for(const s of noRecap)fu.push({html:t('home.summaryMissing',{name:esc(stuName(s.studentId)),date:fmtDM(s.date)}),act:'recap',id:s.id});
  for(const x of who.filter(x=>!x.e&&S.sessions.some(z=>z.studentId===x.s.id)).slice(0,3))fu.push({html:t('ov.noNextFu',{name:esc(x.s.first)}),act:'plan',id:x.s.id});
  const tutor=!!(CLOUD.ctx&&CLOUD.ctx.role==='tutor');
  const unbTop=tutor?[]:active.map(s=>({s,tt:stuTotals(s.id)})).filter(x=>x.tt.unb>0).sort((a,b)=>b.tt.unb-a.tt.unb).slice(0,2);
  for(const x of unbTop)fu.push({html:t('home.unbilledLead',{name:esc(x.s.first),h:money(x.tt.unb)}),act:'invoice',id:x.s.id});
  for(const i of S.invoices.filter(invIsOverdue).slice(0,2))fu.push({html:t('home.overdueInv',{no:esc(i.no),name:esc(stuName(i.studentId))}),act:'inv',id:i.id});

  // first-run checklist
  const st=S.settings;
  const steps=[
    {ok:!!st.teacher,label:t('ob.profile'),act:'set:profile'},
    {ok:!!(st.iban||st.paypal),label:t('ob.payment'),act:'set:billing'},
    {ok:active.length>0,label:t('ob.student'),act:'newstudent'},
    {ok:S.sessions.length>0||S.recs.length>0,label:t('ob.lesson'),act:'newlesson'},
  ];
  const doneN=steps.filter(x=>x.ok).length;
  const obCard=doneN<steps.length&&!(st.dismissed&&st.dismissed.onboarding)&&!(CLOUD.ctx&&CLOUD.ctx.role==='tutor')?`<div class="card mb onboard">
    <div class="cardhead"><h2>${t('ob.title')}</h2><span class="tiny">${doneN}/${steps.length}</span></div>
    <p class="muted mb8" style="font-size:13.5px">${t('ob.lead')}</p>
    <button type="button" class="btn small ghost ob-story mb8" data-story><svg viewBox="0 0 24 24"><path d="M8 5.5v13l11-6.5z"/></svg>${t('story.watch')}</button>
    ${steps.map((x,i)=>'<div class="obrow'+(x.ok?' done':'')+'"><span class="obtick">'+(x.ok?IC.check:(i+1))+'</span><span class="grow">'+x.label+'</span>'+(x.ok?'':x.act.startsWith('set:')?'<button class="btn small" data-set="'+x.act.slice(4)+'">'+t('ob.open')+'</button>':x.act.startsWith('go:')?'<button class="btn small" data-go="'+x.act.slice(3)+'">'+t('ob.open')+'</button>':'<button class="btn small primary" data-a="'+x.act+'">'+IC.plus+'</button>')+'</div>').join('')}
    <button class="btn small ghost mt" data-dismiss="onboarding">${t('ob.later')}</button>
  </div>`:'';

  let nextHtml;
  if(next){
    const s=stu(next.studentId);const ms0=evStart(next);const c=countdown(ms0,next.dur);
    const nm=meetOf(next);
    const gm=next.gid?groupMembers(next):[];const isG=gm.length>1;
    nextHtml=`<div class="nextcard" role="button" tabindex="0" data-evt="${esc(next.id)}">
      ${isG?'<span class="avstack">'+gm.slice(0,3).map(m=>avatarHtml(stu(m.studentId)||s)).join('')+'</span>':avatarHtml(s)}
      <span class="nc-main"><span class="nc-k">${t('ov.next')}</span>
        <span class="nc-name">${isG?esc(groupLabel(gm))+'<small> · '+esc(t('grp.n',{n:gm.length}))+'</small>':esc(s.first+' '+(s.last||''))+(s.subject?'<small> · '+esc(s.subject)+'</small>':'')}</span>
        <span class="nc-when">${esc(dayLabel(next.date))}, ${next.time}–${sesEnd(next)}</span></span>
      <span class="nc-side"><span class="nc-count${c.now?' now':''}" data-countdown="${ms0}" data-dur="${next.dur}">${esc(c.txt)}</span>
      ${nm?`<button class="nc-join" data-meet="${esc(nm)}" title="${esc(t('ev.join'))}">${IC.video} ${t('ev.join')}</button>`:''}</span>
    </div>`;
  }else nextHtml=`<p class="hero-empty">${active.length?t('ov.nothingPlanned'):t('stu.addFirst')}</p>`;

  const whoCard=active.length?`<div class="card mb">
    <div class="cardhead"><h2>${t('ov.who')}</h2><span class="tiny">${active.length} ${t('stu.active')}</span></div>
    ${who.map(({s,e})=>e
      ?`<div class="listrow click whorow" data-evt="${esc(e.id)}">${avatarHtml(s)}<div class="grow"><div class="title">${esc(s.first+' '+(s.last||''))}${subjPill(s)}</div><div class="meta">${esc(dayLabel(e.date))}, ${e.time}–${sesEnd(e)}</div></div>`+
        (()=>{const c=countdown(evStart(e),e.dur);return `<span class="whorel${c.now?' now':''}" data-countdown="${evStart(e)}" data-dur="${e.dur}">${esc(c.txt)}</span>`})()+
        (meetOf(e)?`<button class="iconbtn" data-meet="${esc(meetOf(e))}" title="${esc(t('ev.join'))}">${IC.video}</button>`:'')+`</div>`
      :`<div class="listrow whorow none">${avatarHtml(s)}<div class="grow"><div class="title">${esc(s.first+' '+(s.last||''))}${subjPill(s)}</div><div class="meta warnline">${t('ov.noNext')}</div></div><button class="btn small" data-a="planfor" data-stu="${s.id}">${IC.cal} ${t('stu.schedule')}</button></div>`
    ).join('')}
  </div>`:'';

  return `
  ${!canStore?'<div class="banner warn">⚠ '+t('storage.warn')+'</div>':''}
  ${trialBanner()}<div class="hero">
    <div class="date">${esc(fmtDateLong(T))}</div>
    <h1>${greet}${esc(name)}.</h1>
    ${nextHtml}
    <div class="row">
      <div><div class="hv">${fmtH(ws.mins)}</div><div class="hl">${t('home.weekH')}</div></div>
      ${tutor?'':`<div><div class="hv">${money(ms.amt)}</div><div class="hl">${t('ov.monthE',{month:esc(monthName)})}</div></div>
      <div><div class="hv">${money(outstandingTotal())}</div><div class="hl">${t('home.outstanding')}</div></div>`}
      <div><div class="hv">${active.length}</div><div class="hl">${t('home.activeStu')}</div></div>
    </div>
  </div>
  ${homeReminderCta()}
  <div class="homegrid">
    <div>
      ${obCard}
      <div class="card marge mb">
        <div class="cardhead"><h2>${t('home.today')}</h2><span class="tiny">${esc(fmtDM(T))}</span></div>
        ${td.length?collapseGroups(td).map(e=>evtLine(e,false)).join(''):'<div class="empty"><div class="big"><svg viewBox="96 96 320 340" width="46" height="48" aria-hidden="true" style="display:inline-block"><path d="M352 112c-72 8-136 56-168 120-20.8 41.6-28.8 88-31.2 132.8l-4.8 39.2c-.8 7.2 7.2 12.8 13.6 8.8l33.6-20.8C240 368 284 344 316 308c44-48 56-128 36-196z" fill="currentColor"/></svg></div><p>'+t('home.noLessonsToday')+'</p></div>'}
      </div>
      ${whoCard}
      <div class="card">
        <div class="cardhead"><h2>${t('home.upcoming')}</h2><button class="btn small ghost" data-go="calendar">${t('home.viewCal')}</button></div>
        ${upc.length?collapseGroups(upc).map(e=>evtLine(e,true)).join(''):'<div class="empty"><p>'+t('home.emptyUpcoming')+'</p><button class="btn primary" data-a="newlesson">'+IC.plus+' '+t('home.addLesson')+'</button></div>'}
      </div>
    </div>
    <div>
      ${homeRemindersCard()}
      <div class="card marge mb">
        <div class="cardhead"><h2>${t('home.followups')}</h2></div>
        ${fu.length?fu.map(f=>'<div class="listrow click" data-fu="'+f.act+':'+f.id+'"><div class="grow"><div class="meta" style="font-size:13px;color:var(--ink)">'+f.html+'</div></div>'+IC.right.replace('<svg','<svg style="width:15px;height:15px;stroke:var(--ink3);fill:none;stroke-width:2"')+'</div>').join(''):'<div class="empty"><p>'+t('home.allClear')+' ✓</p></div>'}
      </div>
      <div class="card">
        <div class="cardhead"><h2>${t('home.quick')}</h2></div>
        <div class="row">
          <button class="btn" data-a="newlesson">${IC.cal} ${t('home.addLesson')}</button>
          <button class="btn" data-a="newstudent">${IC.plus} ${t('home.addStudent')}</button>
          <button class="btn" data-a="newinvoice">${IC.doc} ${t('home.newInvoice')}</button>
        </div>
      </div>
    </div>
  </div>`;
}
